import React from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Scroll-reveal wrapper. Fires once on first viewport entry; never replays.
 * Disabled entirely under prefers-reduced-motion.
 * variant="rise": quick fade-up. For headings and text blocks.
 * variant="card": fade-up with a subtle scale-settle and a softer, longer
 * ease. For cards and grid items.
 * variant="ack": fast, subtle acknowledgment fade for filter-change replays.
 * delay (seconds) staggers siblings.
 * mode="view" (default): animate on viewport entry (once, -60px margin).
 *   All existing usages are unaffected.
 * mode="mount": animate on mount instead — used to replay the ack on every
 *   filter change by remounting the wrapper via a filter-derived key.
 */
const VARIANTS = {
  rise: {
    initial: { opacity: 0, y: 12 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] },
  },
  card: {
    initial: { opacity: 0, y: 16, scale: 0.96 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] },
  },
  ack: {
    initial: { opacity: 0, y: 6 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.2, ease: "easeOut" },
  },
};

export default function Reveal({ children, delay = 0, className, variant = "rise", mode = "view" }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  const v = VARIANTS[variant] || VARIANTS.rise;
  if (mode === "mount") {
    return (
      <motion.div
        className={className}
        initial={v.initial}
        animate={v.whileInView}
        transition={{ ...v.transition, delay }}
      >
        {children}
      </motion.div>
    );
  }
  return (
    <motion.div
      className={className}
      initial={v.initial}
      whileInView={v.whileInView}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ ...v.transition, delay }}
    >
      {children}
    </motion.div>
  );
}